// src/component/CommunityChatPage.js
import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
} from "@mui/material";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { db, auth } from "../api/firebase";
import { onAuthStateChanged } from "firebase/auth";

import PrefectureDialog from "./PrefectureDialog";
import PrefectureChat from "./PrefectureChat";
import { AppThemeContext } from "./AppShell";
import Chat20251222 from "./Chat20251222";

const DEFAULT_COMMUNITY_CFG = {
  enabled: true,
  allowAdminBypass: true,
  maxPaymentPhase: 3,
  allowedPaymentPhases: [3],
  allowSekaiMura: false, // ★追加
};

const CommunityChatPage = ({ theme: themeProp }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const initialMode =
    (location.state && location.state.chatMode) || "prefecture";

  const [user, setUser] = useState(null);
  const [prefectureDialogOpen, setPrefectureDialogOpen] = useState(false);
  const [mode, setMode] = useState(initialMode);

  // ★ 読み込み（ユーザー）
  const [loading, setLoading] = useState(true);

  // ★ systemConfig（交流チャット表示設定）
  const [communityCfg, setCommunityCfg] = useState(DEFAULT_COMMUNITY_CFG);
  const [loadingCommunityCfg, setLoadingCommunityCfg] = useState(true);

  // ★ 権限判定結果
  const [canAccessCommunityChat, setCanAccessCommunityChat] = useState(false);

  // ★ AppShell からテーマを取得（なければ props / デフォルト）
  const themeFromContext = useContext(AppThemeContext) || null;

  // ✅ ここ重要：context が “空” のときは props を採用する
  const theme = useMemo(() => {
    const hasContextTheme =
      themeFromContext && Object.keys(themeFromContext).length > 0;
    if (hasContextTheme) return themeFromContext;
    if (themeProp && Object.keys(themeProp).length > 0) return themeProp;
    return {};
  }, [themeFromContext, themeProp]);

  const accent = theme.railActive || theme.bottomSelected || "#5A35FF";
  const border = theme.bottomBorder || "rgba(0,0,0,0.08)";
  const textBase = theme.railText || "rgba(60,40,120,0.85)";
  const surface = theme.surfaceBg || "#FFFFFF";
  const appBg = theme.appBg || "#F5F5F5";

  // location.state.chatMode が変わったときも追従
  useEffect(() => {
    if (location.state && location.state.chatMode) {
      setMode(location.state.chatMode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state && location.state.chatMode]);

  // ─────────────────────────────
  // systemConfig/communityChatVisibility を購読
  // ─────────────────────────────
  useEffect(() => {
    setLoadingCommunityCfg(true);

    const normalizeMax = (v) => {
      const n = Math.floor(Number(v));
      if (!Number.isFinite(n) || n < 1)
        return DEFAULT_COMMUNITY_CFG.maxPaymentPhase;
      return Math.min(n, 50);
    };

    const ref = doc(db, "systemConfig", "communityChatVisibility");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        const max = normalizeMax(
          data.maxPaymentPhase ?? DEFAULT_COMMUNITY_CFG.maxPaymentPhase
        );

        const allowed = Array.isArray(data.allowedPaymentPhases)
          ? data.allowedPaymentPhases
              .map((x) => Number(x))
              .filter((n) => Number.isFinite(n) && n >= 1 && n <= max)
              .sort((a, b) => a - b)
          : DEFAULT_COMMUNITY_CFG.allowedPaymentPhases;

        setCommunityCfg({
          enabled: data.enabled !== false,
          allowAdminBypass: data.allowAdminBypass !== false,
          maxPaymentPhase: max,
          allowedPaymentPhases: allowed || [],
          allowSekaiMura: data.allowSekaiMura === true, // ★追加
        });
        setLoadingCommunityCfg(false);
      },
      (err) => {
        console.error("communityChatVisibility onSnapshot error:", err);
        // 取得失敗時は安全側へ（見せない）
        setCommunityCfg({
          enabled: false,
          allowAdminBypass: true,
          maxPaymentPhase: DEFAULT_COMMUNITY_CFG.maxPaymentPhase,
          allowedPaymentPhases: [],
          allowSekaiMura: false, // ★追加
        });
        setLoadingCommunityCfg(false);
      }
    );

    return () => unsub();
  }, []);

  // ─────────────────────────────
  // ユーザー取得（users/{uid}）
  // auth の確定を待ってから users/{uid} を onSnapshot で追従
  // ─────────────────────────────
  useEffect(() => {
    setLoading(true);

    let userUnsub = null; // users/{uid} の購読解除関数

    const authUnsub = onAuthStateChanged(auth, (firebaseUser) => {
      // ユーザーが変わるたびに、前の購読を止める
      if (userUnsub) {
        userUnsub();
        userUnsub = null;
      }

      if (!firebaseUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      const userRef = doc(db, "users", firebaseUser.uid);

      // ✅ users/{uid} をリアルタイム購読
      userUnsub = onSnapshot(
        userRef,
        (snap) => {
          if (snap.exists()) {
            setUser(snap.data());
          } else {
            setUser(null);
          }
          setLoading(false);
        },
        (err) => {
          console.error("users/{uid} onSnapshot error:", err);
          setUser(null);
          setLoading(false);
        }
      );
    });

    return () => {
      if (userUnsub) userUnsub();
      authUnsub();
    };
  }, []);

  // ─────────────────────────────
  // 権限判定：enabled × (adminBypass or paymentsPhase交差)
  // ─────────────────────────────
  useEffect(() => {
    if (loading || loadingCommunityCfg) return;

    if (!user) {
      setCanAccessCommunityChat(false);
      return;
    }

    // systemConfig が停止なら常に不可
    if (!communityCfg.enabled) {
      setCanAccessCommunityChat(false);
      return;
    }

    const isAdmin = !!user.isAdmin;
    if (communityCfg.allowAdminBypass && isAdmin) {
      setCanAccessCommunityChat(true);
      return;
    }

    // ★ セカイムラ認証で解放（Admin側でONのときのみ）
    if (communityCfg.allowSekaiMura && user.isSekaimura === true) {
      setCanAccessCommunityChat(true);
      return;
    }

    const payments = Array.isArray(user.payments) ? user.payments : [];
    const terms = payments
      .map((p) => (typeof p === "number" ? Number(p) : Number(p?.paymentPhase)))
      .filter((n) => Number.isFinite(n));

    const allowedSet = new Set(
      (communityCfg.allowedPaymentPhases || []).map((n) => Number(n))
    );

    const ok = terms.some((t) => allowedSet.has(Number(t)));
    setCanAccessCommunityChat(ok);
  }, [loading, loadingCommunityCfg, user, communityCfg]);

  const handlePrefectureUpdated = () => {
    setUser((prev) => ({
      ...(prev || {}),
      isRegisterPrefecture: true,
    }));
  };

  const getPrefectureLogicalName = (prefecture) => {
    const prefectureMaps = {
      北海道: "hokkaido",
      青森県: "aomori",
      岩手県: "iwate",
      宮城県: "miyagi",
      秋田県: "akita",
      山形県: "yamagata",
      福島県: "fukushima",
      茨城県: "ibaraki",
      栃木県: "tochigi",
      群馬県: "gunma",
      埼玉県: "saitama",
      千葉県: "chiba",
      東京都: "tokyo",
      神奈川県: "kanagawa",
      新潟県: "niigata",
      富山県: "toyama",
      石川県: "ishikawa",
      福井県: "fukui",
      山梨県: "yamanashi",
      長野県: "nagano",
      岐阜県: "gifu",
      静岡県: "shizuoka",
      愛知県: "aichi",
      三重県: "mie",
      滋賀県: "shiga",
      京都府: "kyoto",
      大阪府: "osaka",
      兵庫県: "hyogo",
      奈良県: "nara",
      和歌山県: "wakayama",
      鳥取県: "tottori",
      島根県: "shimane",
      岡山県: "okayama",
      広島県: "hiroshima",
      山口県: "yamaguchi",
      徳島県: "tokushima",
      香川県: "kagawa",
      愛媛県: "ehime",
      高知県: "kochi",
      福岡県: "fukuoka",
      佐賀県: "saga",
      長崎県: "nagasaki",
      熊本県: "kumamoto",
      大分県: "oita",
      宮崎県: "miyazaki",
      鹿児島県: "kagoshima",
      沖縄県: "okinawa",
      "海外（アメリカ）": "amerika",
      "海外（その他）": "foreign",
    };
    return prefectureMaps[prefecture] || prefecture;
  };

  const prefectureToRegionMap = {
    北海道: { region: "東北・北海道", groupId: "tohoku-hokkaido" },
    青森県: { region: "東北・北海道", groupId: "tohoku-hokkaido" },
    岩手県: { region: "東北・北海道", groupId: "tohoku-hokkaido" },
    宮城県: { region: "東北・北海道", groupId: "tohoku-hokkaido" },
    秋田県: { region: "東北・北海道", groupId: "tohoku-hokkaido" },
    山形県: { region: "東北・北海道", groupId: "tohoku-hokkaido" },
    福島県: { region: "東北・北海道", groupId: "tohoku-hokkaido" },
    茨城県: { region: "関東", groupId: "kanto" },
    栃木県: { region: "関東", groupId: "kanto" },
    群馬県: { region: "関東", groupId: "kanto" },
    埼玉県: { region: "関東", groupId: "kanto" },
    千葉県: { region: "関東", groupId: "kanto" },
    東京都: { region: "関東", groupId: "kanto" },
    神奈川県: { region: "関東", groupId: "kanto" },
    新潟県: { region: "中部", groupId: "chubu" },
    富山県: { region: "中部", groupId: "chubu" },
    石川県: { region: "中部", groupId: "chubu" },
    福井県: { region: "中部", groupId: "chubu" },
    山梨県: { region: "中部", groupId: "chubu" },
    長野県: { region: "中部", groupId: "chubu" },
    岐阜県: { region: "中部", groupId: "chubu" },
    静岡県: { region: "中部", groupId: "chubu" },
    愛知県: { region: "中部", groupId: "chubu" },
    三重県: { region: "近畿", groupId: "kinki" },
    滋賀県: { region: "近畿", groupId: "kinki" },
    京都府: { region: "近畿", groupId: "kinki" },
    大阪府: { region: "近畿", groupId: "kinki" },
    兵庫県: { region: "近畿", groupId: "kinki" },
    奈良県: { region: "近畿", groupId: "kinki" },
    和歌山県: { region: "近畿", groupId: "kinki" },
    鳥取県: { region: "中国", groupId: "chugoku" },
    島根県: { region: "中国", groupId: "chugoku" },
    岡山県: { region: "中国", groupId: "chugoku" },
    広島県: { region: "中国", groupId: "chugoku" },
    山口県: { region: "中国", groupId: "chugoku" },
    徳島県: { region: "四国", groupId: "shikoku" },
    香川県: { region: "四国", groupId: "shikoku" },
    愛媛県: { region: "四国", groupId: "shikoku" },
    高知県: { region: "四国", groupId: "shikoku" },
    福岡県: { region: "九州・沖縄", groupId: "kyushu-okinawa" },
    佐賀県: { region: "九州・沖縄", groupId: "kyushu-okinawa" },
    長崎県: { region: "九州・沖縄", groupId: "kyushu-okinawa" },
    熊本県: { region: "九州・沖縄", groupId: "kyushu-okinawa" },
    大分県: { region: "九州・沖縄", groupId: "kyushu-okinawa" },
    宮崎県: { region: "九州・沖縄", groupId: "kyushu-okinawa" },
    鹿児島県: { region: "九州・沖縄", groupId: "kyushu-okinawa" },
    沖縄県: { region: "九州・沖縄", groupId: "kyushu-okinawa" },
    "海外（アメリカ）": { region: "海外", groupId: "overseas" },
    "海外（その他）": { region: "海外", groupId: "overseas" },
  };

  const getRegionInfo = (prefecture) => {
    return (
      prefectureToRegionMap[prefecture] || {
        region: "不明",
        groupId: "unknown",
      }
    );
  };

  // 先にローディング
  if (loading || loadingCommunityCfg) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: appBg,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>ユーザー情報を取得できませんでした。</Typography>
      </Box>
    );
  }

  // ★ 権限がない場合：チャットは表示しない（URL直打ち対策）
  if (!canAccessCommunityChat) {
    const isForceDisabled = !communityCfg.enabled; // 管理側でOFF

    return (
      <Box
        sx={{
          px: { xs: 1.5, sm: 3 },
          pt: { xs: 2, sm: 3 },
          pb: { xs: 9, sm: 4 },
          minHeight: "100%",
          display: "flex",
          flexDirection: "column",
          background: appBg,
        }}
      >
        {/* ヘッダー */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            borderBottom: `1px solid ${border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
            bgcolor: surface,
            borderRadius: 2,
            boxShadow: {
              xs: "0 4px 12px rgba(15,10,40,0.06)",
              sm: "0 6px 16px rgba(15,10,40,0.08)",
            },
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                bgcolor: `${accent}15`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ForumRoundedIcon sx={{ color: accent, fontSize: 22 }} />
            </Box>

            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  fontSize: 11,
                  opacity: 0.7,
                  color: textBase,
                }}
              >
                COMMUNITY LOUNGE
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  letterSpacing: "0.03em",
                  color: textBase,
                }}
              >
                交流ラウンジ
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* 案内カード */}
        <Box
          sx={{
            flexGrow: 1,
            mt: 2,
            bgcolor: surface,
            borderRadius: 2,
            boxShadow: { xs: "none", sm: "0 10px 30px rgba(15,10,40,0.08)" },
            p: { xs: 2.2, sm: 3 },
          }}
        >
          <Typography sx={{ fontWeight: 800, color: textBase }}>
            {isForceDisabled
              ? "現在表示できません。"
              : "参照権限がありません。"}
          </Typography>

          <Typography
            variant="body2"
            sx={{ mt: 1.2, color: textBase, opacity: 0.78, lineHeight: 1.75 }}
          >
            {isForceDisabled ? (
              <>
                現在、交流チャットは一時的に利用できません。
                <br />
                時間をおいてお試しください。
              </>
            ) : (
              <>
                このチャットは、お支払い完了後に閲覧できるようになります。
                <br />
                2026のメンバーの方は、2025年12月21日以降に解放予定です。
              </>
            )}
          </Typography>

          {!isForceDisabled && (
            <Button
              variant="contained"
              onClick={() => navigate("/mm2026join-content")}
              sx={{ mt: 2, borderRadius: 999, fontWeight: 800 }}
            >
              お支払いページへ
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  // ★ 居住地未設定なら先に設定させる（権限OKのユーザーのみ）
  if (!user.prefecture) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          居住地
        </Typography>
        {user.isRegisterPrefecture ? (
          <Box>
            <Typography variant="body1" sx={{ fontWeight: "bold", mb: 0.5 }}>
              {user.prefecture}
            </Typography>
            <Typography variant="caption">
              ※一度決定した居住地は変更できません。
            </Typography>
          </Box>
        ) : (
          <Button
            variant="contained"
            onClick={() => setPrefectureDialogOpen(true)}
          >
            居住地を編集
          </Button>
        )}
        <PrefectureDialog
          open={prefectureDialogOpen}
          handleClose={() => setPrefectureDialogOpen(false)}
          onPrefectureUpdated={handlePrefectureUpdated}
        />
      </Box>
    );
  }

  //   const renderChat = () => {
  //     if (mode === "prefecture") {
  //       const groupId = getPrefectureLogicalName(user.prefecture);
  //       return (
  //         <PrefectureChat
  //           key={`pref-${groupId}`}
  //           groupId={groupId}
  //           region={user.prefecture}
  //         />
  //       );
  //     }
  //     if (mode === "region") {
  //       const regionInfo = getRegionInfo(user.prefecture);
  //       return (
  //         <PrefectureChat
  //           key={`region-${regionInfo.groupId}`}
  //           groupId={regionInfo.groupId}
  //           region={regionInfo.region}
  //         />
  //       );
  //     }
  //     if (mode === "world") {
  //       return <PrefectureChat key="world-allWorld" groupId="allWorld" region="全世界" />;
  //     }
  //     return null;
  //   };

  const renderChat = () => {
    if (mode === "prefecture") {
      const groupId = getPrefectureLogicalName(user.prefecture);
      return (
        <Chat20251222
          key={`chat20251222-pref-${groupId}`}
          groupId={groupId}
          region={user.prefecture}
        />
      );
    }

    if (mode === "region") {
      const regionInfo = getRegionInfo(user.prefecture);
      return (
        <Chat20251222
          key={`chat20251222-region-${regionInfo.groupId}`}
          groupId={regionInfo.groupId}
          region={regionInfo.region}
        />
      );
    }

    if (mode === "world") {
      return (
        <Chat20251222
          key="chat20251222-world-allWorld"
          groupId="allWorld"
          region="全世界"
        />
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        px: { xs: 1.5, sm: 3 },
        pt: { xs: 2, sm: 3 },
        pb: { xs: 9, sm: 4 },
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        background: appBg,
      }}
    >
      {/* ヘッダー */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          borderBottom: `1px solid ${border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
          bgcolor: surface,
          borderRadius: 2,
          boxShadow: {
            xs: "0 4px 12px rgba(15,10,40,0.06)",
            sm: "0 6px 16px rgba(15,10,40,0.08)",
          },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              bgcolor: `${accent}15`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ForumRoundedIcon sx={{ color: accent, fontSize: 22 }} />
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                fontSize: 11,
                opacity: 0.7,
                color: textBase,
              }}
            >
              COMMUNITY LOUNGE
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 800,
                letterSpacing: "0.03em",
                color: textBase,
              }}
            >
              交流ラウンジ
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.5,
                color: textBase,
                opacity: 0.7,
              }}
            >
              {mode === "prefecture" && `${user.prefecture} の県チャット`}
              {mode === "region" &&
                `${getRegionInfo(user.prefecture).region} の地域チャット`}
              {mode === "world" && "全世界のみんなとつながるチャット"}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* チャット本体 */}
      <Box
        sx={{
          flexGrow: 1,
          mt: 2,
          bgcolor: surface,
          borderRadius: 2,
          boxShadow: { xs: "none", sm: "0 10px 30px rgba(15,10,40,0.08)" },
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {renderChat()}
      </Box>
    </Box>
  );
};

export default CommunityChatPage;
